
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import api from "../api";
import { toast } from "react-toastify";

const EditCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: true,
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Load category details.
  useEffect(() => {
    let cancelled = false;

    const fetchCategory = async () => {
      setLoading(true);

      try {
        const res = await api.get(`categories/${id}`);
        const category = res.data?.data || res.data;

        if (cancelled) return;

        setFormData({
          name: category.name || "",
          description: category.description || "",
          status:
            category.status === 1 ||
            category.status === true ||
            category.status === "1",
        });

        if (category.image) {
          const imageUrl = category.image;

          const fullUrl = imageUrl.startsWith("http")
            ? imageUrl
            : `${(
                process.env.REACT_APP_API_URL ||
                "https://blackend.blackhewzen.com"
              ).replace(/\/api\/?$/, "")}/storage/${imageUrl.replace(
                /^\/?storage\//,
                ""
              ).replace(/^\//, "")}`;

          setExistingImage(fullUrl);
        } else {
          setExistingImage(null);
        }
      } catch (error) {
        console.error("Error loading category:", error);

        if (!cancelled) {
          const errorMessage =
            error.response?.data?.message ||
            "Failed to load category details.";

          setMessage(errorMessage);
          toast.error(errorMessage);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCategory();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Clean up the temporary image URL.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    setMessage("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (file && !file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      e.target.value = "";
      return;
    }

    setImage(file);
    setPreview(file ? URL.createObjectURL(file) : null);

    setErrors((prev) => ({
      ...prev,
      image: undefined,
    }));

    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) return;

    setSaving(true);
    setMessage("");
    setErrors({});

    const form = new FormData();
    form.append("name", formData.name.trim());
    form.append("description", formData.description);
    form.append("status", formData.status ? "1" : "0");

    if (image) {
      form.append("image", image);
    }

    try {
      const res = await api.post(`update-category/${id}`, form, {
        headers: {
          Accept: "application/json",
          // Let Axios set the multipart boundary automatically.
        },
      });

      if (res.data?.status === false) {
        throw new Error(
          res.data.message || "Failed to update category."
        );
      }

      const successMessage =
        res.data?.message || "Category updated successfully!";

      setMessage(successMessage);
      toast.success(successMessage);

      // Navigate back to category management.
      navigate("/manage-category");
    } catch (error) {
      console.error("Update category error:", error);

      const responseData = error.response?.data;

      if (error.response?.status === 422) {
        const validationErrors = responseData?.errors || {};
        setErrors(validationErrors);

        const firstError = Object.values(validationErrors)
          .flat()
          .find(Boolean);

        const errorMessage =
          firstError ||
          responseData?.message ||
          "Please fix the validation errors.";

        setMessage(errorMessage);
        toast.error(errorMessage);
      } else {
        const errorMessage =
          responseData?.message ||
          error.message ||
          "Something went wrong. Please try again.";

        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-xl border bg-[#111111] px-4 py-3 text-white placeholder-gray-500 outline-none transition focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 ${
      errors[field]
        ? "border-red-500"
        : "border-[#3B3320]"
    }`;

  const labelClass = "mb-2 block text-sm font-medium text-gray-200";

  const renderError = (field) => {
    if (!errors[field]) return null;

    const error = Array.isArray(errors[field])
      ? errors[field][0]
      : errors[field];

    return (
      <p className="mt-1.5 text-sm text-red-400">
        {error}
      </p>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#080808] px-4 text-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#D4AF37]/20 border-t-[#D4AF37]" />
          <p className="text-sm text-gray-400">
            Loading category details...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] px-4 py-8 text-white sm:px-6 lg:py-12">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="relative mb-8 overflow-hidden rounded-2xl border border-[#6B541A] bg-gradient-to-br from-[#211B0B] via-[#14120C] to-black p-6 shadow-xl sm:p-8">
          <div className="absolute right-0 top-0 h-36 w-36 rounded-full bg-[#D4AF37]/10 blur-3xl" />

          <div className="relative flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#6B541A] bg-black/50 text-[#D4AF37] transition hover:bg-[#D4AF37] hover:text-black"
            >
              <FaArrowLeft />
            </button>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">
                Category Management
              </p>

              <h1 className="text-2xl font-bold sm:text-3xl">
                Edit Category
              </h1>

              <p className="mt-2 text-sm text-gray-400">
                Update category details, image, and visibility.
              </p>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="rounded-2xl border border-[#3B3320] bg-[#101010] p-5 shadow-2xl sm:p-8">
          {message && (
            <div
              role="status"
              className={`mb-6 rounded-xl border p-4 text-sm ${
                Object.keys(errors).length > 0
                  ? "border-red-500/30 bg-red-500/10 text-red-300"
                  : "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F1D675]"
              }`}
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category Name */}
            <div>
              <label htmlFor="category-name" className={labelClass}>
                Category Name{" "}
                <span className="text-[#D4AF37]">*</span>
              </label>

              <input
                id="category-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                maxLength={255}
                placeholder="Enter category name"
                className={inputClass("name")}
              />

              {renderError("name")}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="category-description"
                className={labelClass}
              >
                Description
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Optional)
                </span>
              </label>

              <textarea
                id="category-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Enter category description"
                className={`${inputClass("description")} min-h-[120px] resize-y`}
              />

              {renderError("description")}
            </div>

            {/* Category Image */}
            <div>
              <label htmlFor="category-image" className={labelClass}>
                Category Image
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Optional)
                </span>
              </label>

              <div className="rounded-xl border border-dashed border-[#6B541A] bg-black/40 p-4 sm:p-5">
                <input
                  id="category-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-400 file:mr-4 file:cursor-pointer file:rounded-lg file:border-0 file:bg-[#D4AF37] file:px-4 file:py-2.5 file:font-semibold file:text-black file:transition hover:file:bg-[#E8C85A]"
                />

                {renderError("image")}

                <div className="mt-5 flex flex-wrap gap-5">
                  {existingImage && !preview && (
                    <div>
                      <p className="mb-2 text-xs font-medium text-gray-400">
                        Current Image
                      </p>

                      <div className="rounded-xl border border-[#6B541A] bg-[#17140C] p-2">
                        <img
                          src={existingImage}
                          alt="Current category"
                          className="h-36 w-36 rounded-lg object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {preview && (
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-xs font-medium text-[#D4AF37]">
                          New Image Preview
                        </p>

                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setPreview(null);

                            const input = document.getElementById(
                              "category-image"
                            );

                            if (input) input.value = "";
                          }}
                          className="text-xs font-medium text-red-400 transition hover:text-red-300"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="rounded-xl border border-[#6B541A] bg-[#17140C] p-2">
                        <img
                          src={preview}
                          alt="New category preview"
                          className="h-36 w-36 rounded-lg object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  Leave the image unchanged to keep the current image.
                </p>
              </div>
            </div>

            {/* Status Toggle */}
            <div className="flex items-center justify-between gap-4 rounded-xl border border-[#3B3320] bg-[#17140C] p-4">
              <div>
                <p className="font-medium text-white">
                  Category Status
                </p>

                <p className="mt-1 text-sm text-gray-400">
                  {formData.status
                    ? "Active and visible to customers"
                    : "Inactive and hidden from customers"}
                </p>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                  className="peer sr-only"
                />

                <span className="h-7 w-12 rounded-full bg-gray-700 transition peer-checked:bg-[#D4AF37] peer-focus-visible:ring-2 peer-focus-visible:ring-[#D4AF37] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#101010]" />

                <span className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5 peer-checked:bg-black" />

                <span className="sr-only">
                  Toggle category status
                </span>
              </label>
            </div>

            {/* Update Button */}
            <button
              type="submit"
              disabled={saving}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#B89427] via-[#E2C253] to-[#B89427] px-6 py-4 font-bold text-black shadow-lg shadow-[#D4AF37]/10 transition duration-200 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 focus:ring-offset-[#101010] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                  Updating Category...
                </>
              ) : (
                <>
                  <FaSave />
                  Update Category
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              Fields marked with{" "}
              <span className="text-[#D4AF37]">*</span> are required.
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-gray-600">
          Category Management System
        </p>
      </div>
    </div>
  );
};

export default EditCategory;